/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';

import { LoggedInContext, WellContextService } from '@well/shared-providers';
import { TermsAndConditionsService } from '../service/terms-and-conditions.service';
import {
  TermsAndConditionsAgreementDtoModel,
  TermsAndConditionsAgreementStatusModel,
  TermsAndConditionsDtoModel,
  TermsAndConditionsTypeEnum,
} from '@well/domain';
import { ErrorService, ErrorSeverityEnum, SharedComponentsModule } from '@irian-ro/shared-components-module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { BsSkeletonComponent } from '@well/iriro-frontend-libs/bs-component-store';
import moment from 'moment/moment';
import { TermsAndConditionsAgreementService } from '../service/terms-and-conditions-agreement.service';
import { MessageService } from 'primeng/api';
import { TermsAndConditionsLocalStorageService } from '@well/shared-components';
import { getSignupRights } from '@well/rights';
import { UserContext } from '@irian-ro/util-user';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { sanitizeReturnUrl } from '@irian-ro/auth-oidc';
import { BsButtonDirective, BsButtonLabelDirective } from '@well/iriro-frontend-libs/bs-component-store';
@Component({
    selector: 'well-terms-and-conditions-accept-page',
    imports: [BsButtonDirective, BsButtonLabelDirective, SharedComponentsModule, TranslateModule, BsSkeletonComponent],
    templateUrl: './terms-and-conditions-accept-page.component.html',
    styleUrl: './terms-and-conditions-accept-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TermsAndConditionsAcceptPageComponent implements OnInit {
  wellContextService = inject(WellContextService);

  loggedInContext: LoggedInContext | null = null;

  userContext = inject(UserContext);

  termsAndConditionsService = inject(TermsAndConditionsService);

  termsAndConditionsAgreementService = inject(TermsAndConditionsAgreementService);

  termsAndConditionsLocalStorageService = inject(TermsAndConditionsLocalStorageService);

  termsAndConditions: TermsAndConditionsDtoModel | null = null;

  errorService = inject(ErrorService);
  messageService = inject(MessageService);

  cdRef = inject(ChangeDetectorRef);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  route = inject(ActivatedRoute);
  translateService = inject(TranslateService);
  private whitelistAuthType = ['authEmail', 'authUser'];

  ngOnInit() {
    this.wellContextService.getLoggedInContext().subscribe((loggedInContext: any) => {
      if (
        loggedInContext.loggedInUser &&
        loggedInContext.loggedInUser.authType &&
        !this.userContext.hasExactRights(getSignupRights())
      ) {
        if (
          this.whitelistAuthType.find(
            (whitelistedAuthType) => whitelistedAuthType === loggedInContext.loggedInUser?.authType
          )
        ) {
          this.loggedInContext = loggedInContext;
          this.termsAndConditionsService
            .getUnacceptedLatestForUserOrEmail(
              loggedInContext.loggedInUser.uuid ?? loggedInContext.loggedInUser.name,
              TermsAndConditionsTypeEnum.PLATFORM
            )
            .pipe(map((termsAndConditionsResource) => termsAndConditionsResource?.data))
            .subscribe((termsAndConditions: any) => {
              if (termsAndConditions) {
                this.termsAndConditions = termsAndConditions;
                this.cdRef.markForCheck();
              }
            });
        }
      }
    });
  }

  accept() {
    const termsAndConditionsAgreementDto = this.createTermsAndConditionsAgreementDto();
    this.termsAndConditionsAgreementService.saveTermsAndConditionsAgreement(termsAndConditionsAgreementDto).subscribe({
      next: () => {
        this.storeTermsAndConditions();
        this.messageService.add({
          severity: ErrorSeverityEnum.success,
          summary: this.translateService.instant('shell-app.termsAndConditions.acceptedMessage'),
          key: 'global-error',
        });
        const returnUrl: string = this.route.snapshot.queryParams['returnUrl'] || '/';
        this.router.navigateByUrl(sanitizeReturnUrl(returnUrl, this.router));
      },
      error: (error) => {
        this.errorService
          .prepareErrorsForDisplay(error, 'shell-app.termsAndConditions.error', 'global-error')
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((res: any) => {
            if (res && res.length > 0) {
              res.forEach((value: any) => {
                this.messageService.add(value);
              });
            }
          });
      },
    });
  }

  private createTermsAndConditionsAgreementDto(): TermsAndConditionsAgreementDtoModel {
    const termsAndConditionsAgreementDtoModel: TermsAndConditionsAgreementDtoModel =
      new TermsAndConditionsAgreementDtoModel();
    if (this.loggedInContext?.loggedInUser?.uuid) {
      termsAndConditionsAgreementDtoModel.userUuid = this.loggedInContext.loggedInUser.uuid;
    } else {
      termsAndConditionsAgreementDtoModel.email = this.loggedInContext?.loggedInUser?.name;
    }
    termsAndConditionsAgreementDtoModel.termsAndConditionsDocumentVersion = this.termsAndConditions?.documentVersion;
    termsAndConditionsAgreementDtoModel.termsAndConditionsDocumentLanguage = this.termsAndConditions?.language;
    termsAndConditionsAgreementDtoModel.validFrom = moment().toDate();

    return termsAndConditionsAgreementDtoModel;
  }

  private storeTermsAndConditions() {
    let termsAndConditionsAgreementStatus: TermsAndConditionsAgreementStatusModel =
      this.termsAndConditionsLocalStorageService.retrieve();

    if (!termsAndConditionsAgreementStatus) {
      termsAndConditionsAgreementStatus = new TermsAndConditionsAgreementStatusModel();
    }
    termsAndConditionsAgreementStatus.lastRequestTimestamp = moment().valueOf();
    termsAndConditionsAgreementStatus.documentType = this.termsAndConditions?.documentType;
    this.termsAndConditionsLocalStorageService.store(termsAndConditionsAgreementStatus);
  }
}
